type Owner = 'human' | 'agent';

interface Stage {
  owner: Owner;
  title: string;
  detail: string;
  without: string;
}

const stages: Stage[] = [
  {
    owner: 'human',
    title: 'Spec',
    detail: 'Agreement on what to build, before an agent touches anything.',
    without: 'every later stage compounds the mistake.',
  },
  {
    owner: 'agent',
    title: 'Vertical slices',
    detail: 'Work cut into thin end-to-end slices that do not block each other.',
    without: 'runs drift outside the range the model is reliable in.',
  },
  {
    owner: 'agent',
    title: 'Feedback loop',
    detail: 'The runner runs the checks (lint, types, tests, browser E2E) and feeds every failure back until they pass.',
    without: 'the signal that the code works arrives late: in CI, or from a tester days later.',
  },
  {
    owner: 'agent',
    title: 'Refactor pass',
    detail: 'Cleanup gets a phase of its own rather than being assumed.',
    without: 'models cut corners and the codebase erodes quietly.',
  },
  {
    owner: 'agent',
    title: 'Agentic QA',
    detail: 'The user journey driven end to end in a real browser.',
    without: 'unit tests miss what the user actually meets.',
  },
  {
    owner: 'human',
    title: 'Review',
    detail: 'Developer review and business sign-off.',
    without: 'nothing catches what judgment catches.',
  },
];

// The rule running through the stages is the diagram: it changes colour exactly
// where the work changes hands, so "humans at both ends, agents in the middle"
// is legible before a word of it is read.
const trackClass = (owner: Owner) =>
  owner === 'agent' ? 'border-accent-500' : 'border-gray-500';

const AfkPipeline = ({ className = '' }: { className?: string }) => (
  <ol className={`grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-6 ${className}`}>
    {stages.map((stage, index) => (
      <li
        key={stage.title}
        className={`relative border-l-2 pl-5 lg:border-l-0 lg:border-t-2 lg:pl-0 lg:pt-6 ${trackClass(stage.owner)}`}
      >
        <span
          aria-hidden="true"
          className={`absolute left-[-5px] top-0 h-2 w-2 rounded-full lg:left-0 lg:top-[-5px] ${
            stage.owner === 'agent' ? 'bg-accent-500' : 'bg-gray-500'
          }`}
        />
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-eyebrow text-gray-400">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span
            className={`font-mono text-eyebrow uppercase ${
              stage.owner === 'agent' ? 'text-accent-400' : 'text-gray-400'
            }`}
          >
            {stage.owner === 'agent' ? 'Agent' : 'Human'}
          </span>
        </div>
        <h3 className="mt-3 font-display text-lg font-medium text-white">{stage.title}</h3>
        <p className="mt-2 text-sm text-gray-300">{stage.detail}</p>
        <p className="mt-3 text-sm text-gray-400">
          <span className="text-gray-300">Without it:</span> {stage.without}
        </p>
      </li>
    ))}
  </ol>
);

export default AfkPipeline;
